import { dimensions, neutralScoreVector, ScoreVector } from '../data/dimensions';
import { CoreEvent, DimensionDeltas, primaryEventDeltas, secondaryChoiceDeltas } from '../data/choiceMaps';
import { repeatMultiplier } from './repeatDecay';

export type ScoreEventInput = {
  sceneId: string;
  category: 'repair' | 'boundary' | 'group' | 'avoidance' | 'meme' | 'pressure';
  gestureId: string;
  coreEvent: CoreEvent;
  secondaryChoiceId?: string;
  modifiers: DimensionDeltas[];
};

export type ScoreResult = {
  scores: ScoreVector;
  repeatCounters: {
    gestureGlobal: Record<string, number>;
  };
};

function addDeltas(scores: ScoreVector, deltas: DimensionDeltas, multiplier: number) {
  for (const dimension of dimensions) {
    scores[dimension] += (deltas[dimension] ?? 0) * multiplier;
  }
}

function averageDeltas(deltas: DimensionDeltas[]): DimensionDeltas {
  const average: DimensionDeltas = {};
  for (const dimension of dimensions) {
    average[dimension] = deltas.reduce((total, delta) => total + (delta[dimension] ?? 0), 0) / deltas.length;
  }
  return average;
}

function centeredDeltas(deltas: DimensionDeltas, baseline: DimensionDeltas): DimensionDeltas {
  const centered: DimensionDeltas = {};
  for (const dimension of dimensions) {
    centered[dimension] = (deltas[dimension] ?? 0) - (baseline[dimension] ?? 0);
  }
  return centered;
}

const primaryRandomBaseline = averageDeltas(Object.values(primaryEventDeltas));
const secondaryRandomBaseline = averageDeltas(Object.values(secondaryChoiceDeltas));

function clampScores(scores: ScoreVector): ScoreVector {
  const next = { ...scores };
  for (const dimension of dimensions) {
    next[dimension] = Math.max(0, Math.min(100, Math.round(next[dimension])));
  }
  return next;
}

export function scoreEvents(events: ScoreEventInput[]): ScoreResult {
  const scores: ScoreVector = { ...neutralScoreVector };
  const gestureGlobal: Record<string, number> = {};

  for (const event of events) {
    const occurrence = (gestureGlobal[event.gestureId] ?? 0) + 1;
    gestureGlobal[event.gestureId] = occurrence;
    const multiplier = repeatMultiplier(occurrence);

    addDeltas(scores, centeredDeltas(primaryEventDeltas[event.coreEvent], primaryRandomBaseline), multiplier);
    if (event.secondaryChoiceId) {
      addDeltas(scores, centeredDeltas(secondaryChoiceDeltas[event.secondaryChoiceId] ?? {}, secondaryRandomBaseline), multiplier);
    }
    for (const modifier of event.modifiers) {
      addDeltas(scores, modifier, 1);
    }
  }

  return {
    scores: clampScores(scores),
    repeatCounters: { gestureGlobal }
  };
}
